import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/services/api';
import type { Plan, SubscriptionTier } from '@/types/api';
import { ArrowLeft, Check, X, MessageSquare, AlertCircle } from 'lucide-react';

const penceToPounds = (pence: number) => (pence / 100).toFixed(2);

// Feature keys are admin-defined (an arbitrary Map<String, Boolean> server-side, not
// a fixed enum) — render whatever keys the real plans actually carry, in a stable
// order shared across every card, rather than a hardcoded label list (C1 fix).
function humanizeFeatureKey(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (c) => c.toUpperCase())
    .replace(/\bAi\b/, 'AI');
}

export default function PlansPage() {
  const queryClient = useQueryClient();

  const {
    data: plans,
    isLoading: plansLoading,
    isError: plansError,
  } = useQuery({
    queryKey: ['plans'],
    queryFn: () => apiClient.getPlans(),
  });

  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: () => apiClient.getSubscription(),
  });

  const changePlanMutation = useMutation({
    mutationFn: (planId: string) => apiClient.changePlan(planId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
    },
  });

  if (plansLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (plansError) {
    return (
      <div className="card p-12 text-center">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Failed to load plans</h3>
        <p className="mt-1 text-sm text-gray-500">Please try again shortly.</p>
      </div>
    );
  }

  const currentPlanId = subscription?.plan.id;

  // Union of feature keys across every plan, in a stable order shared by all cards.
  const featureKeys = Array.from(
    new Set((plans ?? []).flatMap((plan) => Object.keys(plan.features)))
  ).sort();

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/billing" className="text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Choose Your Plan</h1>
          <p className="mt-1 text-sm text-gray-500">
            All prices exclude VAT. Billed monthly.
          </p>
        </div>
      </div>

      {changePlanMutation.isError && (
        <div className="rounded-md bg-red-50 border border-red-200 p-4 text-sm text-red-700">
          Failed to change plan: {(changePlanMutation.error as Error)?.message ?? 'Unknown error'}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {plans?.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            featureKeys={featureKeys}
            isCurrent={plan.id === currentPlanId}
            onSelect={() => changePlanMutation.mutate(String(plan.id))}
            isLoading={changePlanMutation.isPending}
          />
        ))}
      </div>

      {/* Enterprise Upgrade Notice */}
      <div className="card p-6 bg-gradient-to-r from-purple-50 to-primary-50">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            <MessageSquare className="h-6 w-6 text-purple-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-gray-900">Upgrading to Enterprise?</h3>
            <p className="text-sm text-gray-600 mt-1">
              Enterprise includes volume discounts for multiple restaurants. Contact our sales team
              to discuss custom pricing for your business.
            </p>
          </div>
          <a
            href="mailto:sales@time2dine.com"
            className="btn btn-primary h-10 whitespace-nowrap"
          >
            Talk to Sales
          </a>
        </div>
      </div>
    </div>
  );
}

interface PlanCardProps {
  plan: Plan;
  featureKeys: string[];
  isCurrent: boolean;
  onSelect: () => void;
  isLoading: boolean;
}

function PlanCard({ plan, featureKeys, isCurrent, onSelect, isLoading }: PlanCardProps) {
  const tierStyles: Record<SubscriptionTier, string> = {
    BASIC: 'border-gray-200',
    PROFESSIONAL: 'border-primary-500 ring-2 ring-primary-100',
    PREMIUM: 'border-blue-500',
    ENTERPRISE: 'border-purple-500',
  };

  return (
    <div className={`card p-6 flex flex-col ${tierStyles[plan.tier]}`}>
      <div className="text-center mb-6">
        <h3 className="text-lg font-bold text-gray-900">{plan.name}</h3>
        {isCurrent && (
          <span className="inline-block mt-1 text-xs text-primary-600 font-medium">
            ★ CURRENT
          </span>
        )}
        <div className="mt-4">
          <span className="text-3xl font-bold text-gray-900">
            £{penceToPounds(plan.pricePerRestaurantPence)}
          </span>
          <span className="text-gray-500">/restaurant/month</span>
        </div>
      </div>

      <div className="space-y-3 flex-1">
        <FeatureRow
          included
          label={
            plan.maxRestaurants
              ? `Up to ${plan.maxRestaurants} restaurant${plan.maxRestaurants !== 1 ? 's' : ''}`
              : 'Unlimited restaurants'
          }
        />
        {featureKeys.map((key) => (
          <FeatureRow key={key} included={!!plan.features[key]} label={humanizeFeatureKey(key)} />
        ))}
        {plan.tier === 'ENTERPRISE' && (
          <p className="text-sm text-gray-500">Contact for volume pricing</p>
        )}
      </div>

      <div className="mt-6">
        {isCurrent ? (
          <button disabled className="btn btn-outline w-full h-10 cursor-default">
            Current ✓
          </button>
        ) : plan.tier === 'ENTERPRISE' ? (
          <a
            href="mailto:sales@time2dine.com"
            className="btn btn-primary w-full h-10 text-center"
          >
            Contact Sales
          </a>
        ) : (
          <button
            onClick={onSelect}
            disabled={isLoading}
            className="btn btn-primary w-full h-10"
          >
            {isLoading ? 'Processing...' : 'Select Plan'}
          </button>
        )}
      </div>
    </div>
  );
}

function FeatureRow({ included, label }: { included: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2">
      {included ? (
        <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
      ) : (
        <X className="h-4 w-4 text-gray-300 flex-shrink-0" />
      )}
      <span className={included ? 'text-sm text-gray-700' : 'text-sm text-gray-400'}>
        {label}
      </span>
    </div>
  );
}
